<template>
  <div id="app">
    <router-view />
  </div>
</template>
<script>
import { mapState } from 'vuex'
import dsBridgeHelper from '@/utils/dsBridgeHelper'
import { getResolvedDynaOcrInitConfig } from '@/utils/dynaConfig'

export default {
  name: 'NoticeApp',
  data() {
    return {
      appReady: false,
      isInitializingOcr: false,
      lastOcrInitKey: '',
      pendingOcrInitKey: '',
      currentOcrInitKey: ''
    }
  },
  computed: {
    ...mapState('app', ['language']),
    ...mapState('user', ['token', 'tokenJT']),
    loginToken() {
      return this.token || this.tokenJT || ''
    }
  },
  watch: {
    loginToken: {
      handler(token) {
        if (!this.appReady) {
          return
        }

        if (!token) {
          this.resetOcrInitState()
          return
        }

        this.initOcrAfterLogin()
      },
      immediate: true
    },
    language() {
      if (!this.appReady || !this.loginToken) {
        return
      }

      this.initOcrAfterLogin()
    }
  },
  created() {
    console.log('当前运行环境:', process.env.NODE_ENV)
    console.log('VUE_APP_PLATFORM:', process.env.VUE_APP_PLATFORM)
  },
  mounted() {
    const loadingElement = document.getElementById('loading-container')
    if (loadingElement) {
      loadingElement.classList.add('fade-out')
      setTimeout(() => {
        loadingElement.remove()
      }, 1000) // 等待动画完成后再移除元素
    }

    // 仅 bluepay 平台请求相机权限
    this.requestCameraPermissionForBluepay()
    // 获取原生 App 信息
    this.logNativeAppInfo()
    this.appReady = true
    this.initOcrAfterLogin()
  },
  methods: {
    resetOcrInitState() {
      this.lastOcrInitKey = ''
      this.pendingOcrInitKey = ''
      this.currentOcrInitKey = ''
      this.isInitializingOcr = false
    },
    buildOcrInitKey() {
      return `${this.loginToken}::${this.language}`
    },
    initOcrAfterLogin() {
      if (!this.loginToken) {
        return
      }

      if (!dsBridgeHelper.isApp()) {
        console.log('[OCR] 不在原生 APP 内，跳过初始化')
        return
      }

      const currentKey = this.buildOcrInitKey()
      const currentLanguage = this.language
      if (this.isInitializingOcr) {
        this.pendingOcrInitKey = currentKey
        return
      }

      if (this.lastOcrInitKey === currentKey) {
        return
      }

      this.isInitializingOcr = true
      this.currentOcrInitKey = currentKey
      const dynaOcrInitConfig = getResolvedDynaOcrInitConfig()

      dsBridgeHelper
        .initOcrByLanguage(currentLanguage, dynaOcrInitConfig)
        .then(result => {
          if (this.currentOcrInitKey === currentKey) {
            this.lastOcrInitKey = currentKey
          }
          console.log(
            '[OCR] 初始化成功',
            JSON.stringify({
              appLanguage: currentLanguage,
              ocrLanguage: dsBridgeHelper.getOcrLanguageByAppLanguage(currentLanguage),
              result
            })
          )
        })
        .catch(err => {
          console.log('[OCR] 初始化失败:', err.message)
        })
        .finally(() => {
          const pendingOcrInitKey = this.pendingOcrInitKey
          const shouldRetry = pendingOcrInitKey && pendingOcrInitKey !== this.lastOcrInitKey

          this.isInitializingOcr = false
          this.currentOcrInitKey = ''
          this.pendingOcrInitKey = ''

          if (shouldRetry) {
            this.$nextTick(() => {
              this.initOcrAfterLogin()
            })
          }
        })
    },
    /**
     * 仅 bluepay 平台请求相机权限
     */
    requestCameraPermissionForBluepay() {
      console.log('requestCameraPermissionForBluepay：', process.env.VUE_APP_PLATFORM)
      // 限制只有 bluepay 平台才调用
      if (process.env.VUE_APP_PLATFORM !== 'bluepay') {
        console.log('限制只有 bluepay 平台才调用')
        return
      }

      // 检查是否在 APP 内
      if (!dsBridgeHelper.isApp()) {
        console.log('不在 APP 内')
        return
      }

      dsBridgeHelper
        .requestCameraPermission()
        .then(granted => {
          //TODO :获取相机权限之后要做的事
          console.log('Camera permission granted:', granted)
        })
        .catch(err => {
          console.log('Camera permission request failed:', err.message)
        })

      // 请求录音权限
      // dsBridgeHelper
      //   .requestMicrophonePermission()
      //   .then(granted => {
      //     console.log('录音权限:', granted)
      //   })
      //   .catch(err => {
      //     console.log('请求失败:', err.message)
      //   })
    },

    /**
     * 获取并打印原生 App 信息
     * 参考 SyncJsApi.swift 的 getDeviceInfo 方法
     */
    logNativeAppInfo() {
      // 检查是否在 APP 内
      if (!dsBridgeHelper.isApp()) {
        console.log('[AppInfo] 不在原生 APP 内，跳过获取设备信息')
        return
      }

      try {
        const deviceInfo = dsBridgeHelper.getDeviceInfo()
        console.log('========== Native App Info ==========')
        console.log('App 名称:', deviceInfo.appName)
        console.log('App 版本:', deviceInfo.appVersion)
        console.log('Bundle ID:', deviceInfo.bundleId)
        console.log('系统版本:', deviceInfo.systemVersion)
        console.log('设备型号:', deviceInfo.deviceModel)
        console.log('设备名称:', deviceInfo.deviceName)
        console.log('屏幕宽度:', deviceInfo.screenWidth)
        console.log('屏幕高度:', deviceInfo.screenHeight)
        console.log('屏幕缩放:', deviceInfo.screenScale)
        console.log('状态栏高度:', deviceInfo.statusBarHeight)
        console.log('安全区顶部:', deviceInfo.safeAreaTop)
        console.log('安全区底部:', deviceInfo.safeAreaBottom)
        console.log('完整设备信息:', deviceInfo)
        console.log('=====================================')
      } catch (err) {
        console.log('[AppInfo] 获取设备信息失败:', err.message)
      }
    }
  }
}
</script>

<style lang="scss">
// html ::-webkit-scrollbar {
//   width: 0;
//   background: transparent;
// }
#app {
  // font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100%;
}

body {
  font-size: 16px;
}

@media screen and (min-width: 780px) {
  #app {
    width: 780px;
    margin: 0 auto;
  }
  html,
  body {
    font-size: 78px !important;
  }
}

#nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
</style>
